/* eslint-disable operator-linebreak */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { reduxConnect } from '../../common';
import { getCurrentLegalPolicies } from '../../common/actions/legalActions';
import styled from 'styled-components';
import { largeMediaQuery, mediumMediaQuery } from '../../common/utilities/sizeHelpers';

import { IStoreState } from '../common/interfaces/types';

type LegalPolicyScreenProps = {
    countryCode: string;
    languageCode: string;
    appInstanceId: string;
    legalBaseUrl: string;
    authId: string;
    secret: string;
    stdParameters: Record<string, any>;
    getCurrentLegalPolicies: typeof getCurrentLegalPolicies;
    currentLegalPolicies: {
        pp: {
            url: string;
        };
        tou: {
            url: string;
        };
    };
}

const fallBackLegal = {
    tou: 'https://policies.weatherbug.net/documents/policies/TermsOfUse/us-en/TOU-US-EN-20180523-00.html',
    pp: 'https://policies.weatherbug.net/documents/policies/PrivacyPolicy/us-en/PP-US-EN-20180523-00.html'
};

const privacyPolicyIframe = 'wbweblegalprivacypolicy';
const rightsRegardingPersonalInformationSection = 'rightregardingpersonalinformation';

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { appState: baseAppState, legal } = storeState || {};
    const { DeviceId: appInstanceId, ActiveLocation: activeLocation } = baseAppState?.appState?.user || {};

    const { languageCode, stdParameters, legalBaseUrl, authId, secret } = baseAppState?.appState || {};
    const { currentLegalPolicies } = legal as any || {};

    return {
        appInstanceId,
        countryCode: activeLocation?.Locality?.CountryIso3Code,
        languageCode,
        stdParameters,
        currentLegalPolicies,
        legalBaseUrl,
        authId,
        secret
    };
};

const mapDispatchToProps = {
    getCurrentLegalPolicies
};

function printPrivacyPolicyInIframe(privacyPolicyIframeId: string, privacyPolicyDocumentSource: string) {
    const ppIframe = document.getElementById(privacyPolicyIframeId) as HTMLIFrameElement | null;

    if (ppIframe && privacyPolicyDocumentSource) {
        const privacyPolicySrcUrlObj = new URL(privacyPolicyDocumentSource);
        const privacyPolicyTargetDomain = `${privacyPolicySrcUrlObj.protocol}//${privacyPolicySrcUrlObj.host}`;
        ppIframe.contentWindow?.postMessage?.('printWeatherBugPrivacyPolicyDoc', privacyPolicyTargetDomain);
    }
}
export const LegalPolicyScreen = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({ 
    countryCode, 
    languageCode, 
    currentLegalPolicies,
    getCurrentLegalPolicies,
    authId,
    secret,
    stdParameters,
    legalBaseUrl,
    appInstanceId }: LegalPolicyScreenProps) => {
    const currentLegalPoliciesRequestedRef = useRef<{
        countryCode: string | null;
        languageCode: string | null;
        appInstanceId: string | null;
    }>({
        countryCode: null,
        languageCode: null,
        appInstanceId: null
    });
    const [iframeHeight, setIframeHeight] = useState(8700);
    const [legalPolicyUrl, setLegalPolicyUrl] = useState('');
    const [isLegalLoadComplete, setIsLegalLoadComplete] = useState(false);
    
    const handleHeightMessageFromPPIfrome = useCallback((event: any) =>  {
        if (!event?.origin?.includes('policies.')) {
            return;
        }
        if (event?.data?.type === 'resize' && event?.data?.height) {
            setIframeHeight(event?.data.height);
        }
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);

        setTimeout(() => {
            window.addEventListener('message', handleHeightMessageFromPPIfrome);
        }, 100);
    
        return () => {
            window.removeEventListener('message', handleHeightMessageFromPPIfrome);
        };
    }, []);

    useEffect(() => {
        if (legalBaseUrl && 
            secret && 
            authId && 
            countryCode &&
            appInstanceId && 
            languageCode && 
            stdParameters && 
            getCurrentLegalPolicies)
        {
            try
            {
                getCurrentLegalPolicies(legalBaseUrl, authId, secret, countryCode, languageCode, stdParameters);
                currentLegalPoliciesRequestedRef.current = {
                    appInstanceId,
                    languageCode,
                    countryCode
                };
            } catch (error) {
                console.error(error);
            }
            setIsLegalLoadComplete(true);
        }
    }, [currentLegalPolicies, countryCode, appInstanceId, languageCode, legalBaseUrl, authId, secret]);

    useEffect(() => {
        if (currentLegalPolicies) {
            const pathname = window.location.pathname;
            const hashLocation = window.location.hash;
            if (pathname.startsWith('/legal/privacy') && currentLegalPolicies?.pp?.url) {
                let privacyPoligyUrl = currentLegalPolicies.pp.url;

                const queryParams = new URLSearchParams(window.location.search);
                const sQueryParam = queryParams.get('s');

                if (sQueryParam) {
                    const userAppInstanceId = queryParams.get('appinstanceid');
        
                    if (userAppInstanceId) {
                        if (privacyPoligyUrl.indexOf('?') !== -1) {
                            privacyPoligyUrl = `${privacyPoligyUrl}&appinstanceid=${userAppInstanceId}&s=${sQueryParam}`;
                        } else {
                            privacyPoligyUrl = `${privacyPoligyUrl}?appinstanceid=${userAppInstanceId}&s=${sQueryParam}`;
                        }
                    }
                }
                if (hashLocation) {
                    privacyPoligyUrl += hashLocation;
                }
                setLegalPolicyUrl(privacyPoligyUrl);
            }
            else if (pathname.startsWith('/legal/terms') && currentLegalPolicies?.tou?.url) {
                let termsOfUseUrl = currentLegalPolicies.tou.url;
                if (hashLocation) {
                    termsOfUseUrl += hashLocation;
                }
                setLegalPolicyUrl(termsOfUseUrl);
            }
        }
    }, [currentLegalPolicies]);

    if (legalPolicyUrl) {
        return (
            <PrivacyPolicyContentContainer className='legal'>
                <PrintContainer>
                    <PrintButton type='button' onClick={() => printPrivacyPolicyInIframe(privacyPolicyIframe, legalPolicyUrl)}>Print</PrintButton>
                </PrintContainer>
                <PrivacyIframeContainer className='iframeContainer'>
                    <Iframe id='wbweblegalprivacypolicy' style={{ height: iframeHeight }} src={legalPolicyUrl} />
                </PrivacyIframeContainer>
            </PrivacyPolicyContentContainer>
        );   
    }

    if (!legalPolicyUrl && isLegalLoadComplete) { // error case we fallback to hardcoded values - we want to always display this
        let legalFallbackUrl = '';
        if (window?.location?.pathname?.startsWith('/legal/privacy')) {
            legalFallbackUrl = fallBackLegal.pp;    
        } else if (window?.location?.pathname?.startsWith('/legal/terms')) {
            legalFallbackUrl = fallBackLegal.tou;
        }

        if (window.location.hash && legalFallbackUrl) {
            legalFallbackUrl = `${fallBackLegal.pp}${window.location.hash}`;
        }
        return (
            <PrivacyPolicyContentContainer id='fallback-legal' className='legal'>
                <PrintContainer>
                    <PrintButton type='button' onClick={() => printPrivacyPolicyInIframe(privacyPolicyIframe, legalPolicyUrl)}>Print</PrintButton>
                </PrintContainer>
                <PrivacyIframeContainer className='iframeContainer'>
                    <Iframe id='wbweblegalprivacypolicy' style={{ height: iframeHeight }} src={legalPolicyUrl} />
                </PrivacyIframeContainer>
            </PrivacyPolicyContentContainer>
        );
    }
    // TODO: revisit this during migration to web 2. ideally we should return a fallback UI with correct PP document
    return null;
});

const PrivacyPolicyContentContainer = styled.div`
    display: flex;
    justify-content: center;
    height: 100%;
    margin-top: -20px;
    gap: 13px;
    flex-direction: column;
`;

const PrivacyIframeContainer = styled.div`
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    display: flex;
    overflow: hidden;
    
    padding: 0 10px;
    max-width: calc(1218px + 8%);
    margin: 0 auto;

    @media ${mediumMediaQuery} {
        padding: 0 15px;
    }

    @media ${largeMediaQuery} {
        padding: 0 4%;
    }
`;

const Iframe = styled.iframe`
    width: 100%;
    overflow: hidden;
    border: none;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;

const PrintContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-direction: row;
    margin-top: 7px;
    height: 39px;
    width: 80%;
    padding: 15px;

`;

const PrintButton = styled.button`
    color: #000000;
    width: 70px;
    font-size: 16px;
    line-height: 21px;
`;